import {useId, useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {formatDistanceToNow} from 'date-fns';
import {FileSpreadsheet, Plus} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  Spinner,
  Switch,
  CopyButton
} from '@kroll-resolver-crisp/ui-components-react';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {TablePanel} from '@/components/ui/table-panel';
import {
  type CustomerFeedbackSource,
  type CustomerFeedbackSourceRequest,
  customerFeedbackSourcesApi,
  customerFeedbackSourcesQueryKey,
  spreadsheetIdFrom,
  useCustomerFeedbackSources
} from '@/api/customerFeedbackSources';
import {getApiErrorMessage} from '@/api/client';
import {toneBadge} from '@/lib/status';

const COLUMN_COUNT = 5;

// The customer's feedback spreadsheets PRI reads itself. Each needs sharing (Viewer) with the
// service account shown here; PRI then reads the tab daily and the changes wait on the submissions
// page for someone to review and confirm.
export function CustomerFeedbackSourcesAdmin() {
  const sources = useCustomerFeedbackSources(true);
  const [editing, setEditing] = useState<CustomerFeedbackSource | 'new' | null>(null);

  if (sources.isLoading) {
    return (
      <div className="text-muted-foreground flex items-center gap-2 text-xs">
        <Spinner /> Loading…
      </div>
    );
  }

  const email = sources.data?.service_account_email;
  const rows = sources.data?.sources ?? [];
  return (
    <div className="flex flex-col gap-4">
      <Alert>
        <AlertTitle>Share each sheet with PRI</AlertTitle>
        <AlertDescription>
          {email ? (
            <span className="flex flex-wrap items-center gap-2">
              Ensure <code className="font-mono">{email}</code> is shared with the sheet as{' '}
              <strong>Viewer</strong>
              <CopyButton value={email} />
            </span>
          ) : (
            'Ensure this environment’s pri-scheduler service account is shared with the sheet as Viewer.'
          )}
        </AlertDescription>
      </Alert>

      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={() => {
            setEditing('new');
          }}>
          <Plus />
          Add sheet
        </Button>
      </div>

      <TablePanel>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Tab</TableHead>
              <TableHead>Last read</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="p-0">
                  <Empty className="py-10">
                    <EmptyHeader>
                      <EmptyMedia variant="icon">
                        <FileSpreadsheet />
                      </EmptyMedia>
                      <EmptyTitle>No feedback sheets</EmptyTitle>
                      <EmptyDescription>
                        No customer feedback sheet has been added yet. Add one to have PRI read it
                        daily.
                      </EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                </TableCell>
              </TableRow>
            ) : null}
            {rows.map((source) => (
              <TableRow key={source.id}>
                <TableCell className="font-medium">{source.name}</TableCell>
                <TableCell>{source.tab}</TableCell>
                <TableCell className="text-muted-foreground">
                  {source.last_fetched_at
                    ? formatDistanceToNow(new Date(source.last_fetched_at), {addSuffix: true})
                    : 'Not yet'}
                </TableCell>
                <TableCell className="max-w-[320px]">
                  {source.enabled ? null : <Badge variant="outline">Disabled</Badge>}
                  {source.last_fetch_error ? (
                    <span className="text-destructive-ink text-xs">{source.last_fetch_error}</span>
                  ) : source.has_pending ? (
                    <Badge {...toneBadge('info')}>Changes to review</Badge>
                  ) : source.enabled ? (
                    source.last_fetched_at ? (
                      <Badge {...toneBadge('success')}>OK</Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )
                  ) : null}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditing(source);
                    }}>
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TablePanel>

      {editing ? (
        <SourceDialog
          source={editing === 'new' ? null : editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      ) : null}
    </div>
  );
}

function SourceDialog({
  source,
  onClose
}: {
  readonly source: CustomerFeedbackSource | null;
  readonly onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const ids = {name: useId(), sheet: useId(), tab: useId(), enabled: useId()};
  const [form, setForm] = useState<CustomerFeedbackSourceRequest>({
    name: source?.name ?? '',
    spreadsheet_id: source?.spreadsheet_id ?? '',
    tab: source?.tab ?? '',
    enabled: source?.enabled ?? true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    const body = {...form, spreadsheet_id: spreadsheetIdFrom(form.spreadsheet_id)};
    try {
      await (source
        ? customerFeedbackSourcesApi.update(source.id, body)
        : customerFeedbackSourcesApi.create(body));
      await queryClient.invalidateQueries({queryKey: customerFeedbackSourcesQueryKey});
      onClose();
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Save failed'));
    } finally {
      setSaving(false);
    }
  }

  const complete = form.name.trim() && form.spreadsheet_id.trim() && form.tab.trim();
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !saving) {
          onClose();
        }
      }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{source ? 'Edit sheet' : 'Add sheet'}</DialogTitle>
          <DialogDescription>
            PRI reads this tab daily. Changes wait on the submissions page for review.
          </DialogDescription>
        </DialogHeader>
        <Field>
          <FieldLabel htmlFor={ids.name}>Name</FieldLabel>
          <Input
            id={ids.name}
            value={form.name}
            placeholder="Falcon bad actors"
            onChange={(e) => {
              setForm({...form, name: e.target.value});
            }}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor={ids.sheet}>Sheet URL or ID</FieldLabel>
          <Input
            id={ids.sheet}
            value={form.spreadsheet_id}
            placeholder="https://docs.google.com/spreadsheets/d/…"
            onChange={(e) => {
              setForm({...form, spreadsheet_id: e.target.value});
            }}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor={ids.tab}>Tab</FieldLabel>
          <Input
            id={ids.tab}
            value={form.tab}
            placeholder="Bad Actor Subs 2026"
            onChange={(e) => {
              setForm({...form, tab: e.target.value});
            }}
          />
          <FieldDescription>
            The tab&rsquo;s exact name. Update it if the customer starts a new tab (e.g. each year).
          </FieldDescription>
        </Field>
        <Field orientation="horizontal">
          <Switch
            id={ids.enabled}
            checked={form.enabled}
            onCheckedChange={(checked) => {
              setForm({...form, enabled: checked});
            }}
          />
          <FieldLabel htmlFor={ids.enabled}>Read daily</FieldLabel>
        </Field>
        {error ? <p className="text-destructive-ink text-xs">{error}</p> : null}
        <DialogFooter>
          <Button variant="ghost" size="sm" disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" disabled={saving || !complete} onClick={() => void save()}>
            {saving ? <Spinner /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
